/**
 * Hero carousel — crossfades product slides, each crediting its maker.
 *
 * Accessibility is the reason this is hand-written rather than a library:
 * only the active slide is visible, focusable and exposed to assistive tech.
 * The rest carry aria-hidden and tabindex="-1", so a screen reader encounters
 * one product at a time instead of five stacked links.
 *
 * Rotation stops on hover, on keyboard focus, when the tab is hidden, and
 * entirely for anyone who prefers reduced motion — an auto-advancing link
 * target is hostile otherwise.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class BunchberryHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.bb-hero__slide')];
    this.dots = [...this.querySelectorAll('[data-bb-dot]')];
    if (this.slides.length < 2) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval) || 6000;
    this.wants = this.dataset.autoplay === 'true';

    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => {
        this.#show(Number(dot.dataset.bbDot));
        this.#stop(); // a deliberate choice shouldn't be overridden a second later
      })
    );

    this.addEventListener('pointerenter', () => this.#stop());
    this.addEventListener('pointerleave', () => this.#start());
    this.addEventListener('focusin', () => this.#stop());
    this.addEventListener('focusout', () => this.#start());

    document.addEventListener('visibilitychange', () =>
      document.hidden ? this.#stop() : this.#start()
    );

    REDUCED.addEventListener?.('change', () => (REDUCED.matches ? this.#stop() : this.#start()));

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #show(next) {
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      // Keep the inactive slides out of the accessibility tree and tab order.
      slide.toggleAttribute('aria-hidden', !active);
      if (active) slide.removeAttribute('tabindex');
      else slide.setAttribute('tabindex', '-1');
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      dot.setAttribute('aria-selected', String(i === next));
    });
    this.index = next;
  }

  #start() {
    if (!this.wants || REDUCED.matches || this.timer) return;
    this.timer = setInterval(
      () => this.#show((this.index + 1) % this.slides.length),
      this.interval
    );
  }

  #stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('bunchberry-hero-carousel')) {
  customElements.define('bunchberry-hero-carousel', BunchberryHeroCarousel);
}
